<i18n locale="en" lang="json">
{
  "download": "Download",
  "noPreview": "this document cannot be previewed",
  "noPdfSupport": "This browser does not support PDFs. Please download the .pdf to view it:",
  "downloadPdf": "Download .pdf"
}
</i18n>
<i18n locale="nl" lang="json">
{
  "download": "Download",
  "noPreview": "Van dit document kan geen voorvertoning worden gemaakt",
  "noPdfSupport": "Deze browser ondersteunt geen PDF's. Download de .pdf om deze te bekijken:",
  "downloadPdf": "Download .pdf"
}
</i18n>

<template>
  <base-dialog
    v-model:is-open="internalIsOpen"
    :title="title || fileName"
    :fullscreen="computedFullscreen"
    :width="computedFullscreen ? undefined : width || '900'"
    content-class="d-flex pa-0 overflow-y-hidden"
    :loading="loadingContent"
    disable-dialog-loader
  >
    <div class="flex-grow-1">
      <v-skeleton-loader
        v-if="loadingContent && hasViewer"
        :type="skeletonLoaderType"
      />
      <template v-if="location && internalIsOpen">
        <object
          v-if="computedDocumentType === 'pdf'"
          :data="locationPdfInline"
          :style="embedStyle as StyleValue"
          type="application/pdf"
          @load="loadingContent = false"
        >
          <p>
            {{ t('noPdfSupport') }}
            <a :href="locationPdfInline">{{ t('downloadPdf') }}</a>
          </p>
        </object>
        <div
          v-else-if="computedDocumentType === 'image'"
          class="fill-height d-flex flex-column justify-center bg-tonedBackground pa-1"
        >
          <img
            :src="location"
            class="white"
            style="max-width: 100%; max-height: 100%; object-fit: contain"
            :alt="title || fileName"
            @load="loadingContent = false"
          />
        </div>
        <div v-else class="fill-height d-flex flex-column justify-center">
          <div class="d-flex justify-center">
            <div style="width: 60%">
              <div class="mb-3 text-center">{{ t('noPreview') }}</div>
              <div class="text-center">
                <base-button
                  v-set-test-id="componentName"
                  large
                  primary
                  :href="location"
                  :icon="faDownload"
                >
                  {{ t('download') }}
                </base-button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
    <template v-for="(_, name) in slots" #[name]="slotData">
      <slot :name="name" v-bind="slotData || {}" />
    </template>
  </base-dialog>
</template>

<script lang="ts" setup>
import { faDownload } from '@fortawesome/pro-regular-svg-icons';
import type { StyleValue } from 'vue';
import { computed, ref, useSlots, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';
import useDialogIsOpen from '@/common/composition/useDialogIsOpen';
import { vSetTestId } from '@/common/components/ActionableComponent';
import type {
  DialogEmits,
  DialogProps,
} from '@/common/components/zvdzo/BaseDialog.types';

type DocumentType = 'pdf' | 'image';

const componentName = 'DocumentViewerDialog';

type Props = DialogProps & {
  isOpen?: boolean;
  location?: string;
  documentType?: DocumentType;
  fullscreen?: boolean;
  width?: string | number;
  title?: string;
  fileName?: string;
};
const props = withDefaults(defineProps<Props>(), { fullscreen: undefined });
const slots = useSlots();
const emit = defineEmits<DialogEmits>();

const { t } = useI18n();
const internalIsOpen = useDialogIsOpen(props, emit);

const locationPdfInline = computed(() => {
  if (!props.location) {
    return undefined;
  }
  const { search } = new URL(
    props.location,
    document.location as unknown as URL,
  );
  return `${props.location}${search ? '&' : '?'}inline=true`;
});

const computedDocumentType = computed<DocumentType | undefined>(() => {
  if (props.documentType) {
    return props.documentType;
  }
  if (props.fileName) {
    if (props.fileName.toLowerCase().endsWith('.pdf')) {
      return 'pdf';
    }
    if (props.fileName.match(/\.(jpg|jpeg|png|gif|tiff)$/i)) {
      return 'image';
    }
  }
  return undefined;
});

const documentTypesWithViewers: DocumentType[] = ['pdf', 'image'];
const hasViewer = computed<boolean>(
  () =>
    !!computedDocumentType.value &&
    documentTypesWithViewers.includes(computedDocumentType.value),
);

const display = useDisplay();
const computedFullscreen = computed<boolean>(() => {
  if (props.fullscreen === undefined) {
    return display.xs.value;
  }
  return props.fullscreen;
});

const loadingContent = ref(false);
const embedStyle = computed(() => {
  const defaultStyle = { border: 0 };
  if (loadingContent.value) {
    return {
      ...defaultStyle,
      width: '1px',
      height: '1px',
      position: 'absolute',
    };
  }
  if (computedFullscreen.value) {
    return { ...defaultStyle, width: '100%', height: '100%' };
  }
  return { ...defaultStyle, width: '100%', height: '80vh' };
});

const skeletonLoaderType = computed(() => {
  if (computedDocumentType.value === 'pdf') {
    return 'table-heading, list-item-two-line, list-item-three-line, article';
  }
  return 'image';
});

watch(
  () => internalIsOpen.value && hasViewer.value && !!props.location,
  newContent => {
    if (newContent) {
      loadingContent.value = true;
    }
  },
  { immediate: true },
);
watch(
  () => props.isOpen,
  () => {
    internalIsOpen.value = props.isOpen;
  },
);
</script>
